import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { useNotification, useWarnAboutChange } from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { DeleteGroupDialog } from "@/pages/event-groups/components/delete-group-dialog";
import { EventGroupForm } from "@/pages/event-groups/components/event-group-form";
import { GroupMembersEditor } from "@/pages/event-groups/components/group-members-editor";
import {
  BackLink,
  GROUPS_VIEW,
  GroupGone,
  GroupLoadFailed,
  GroupLoading,
} from "@/pages/event-groups/components/group-page-states";
import { useEventGroupQuery } from "@/pages/event-groups/hooks/use-event-group-query";
import { useEventGroupRepository } from "@/pages/event-groups/hooks/use-event-group-repository";
import { refusalMessage } from "@/pages/event-groups/lib/refusal-message";
import type { EventGroupFormType } from "@/pages/event-groups/schemas/event-group.form";
import type { EventGroup } from "@/types/event-group";

/**
 * The form's starting values. `id` is the group's own, which is also where its photos are stored.
 * A group read can leave `about` or `teasers` out, and the media field needs both.
 */
function formValues(group: EventGroup): Partial<EventGroupFormType> {
  return {
    id: group.id,
    name: group.name,
    about: group.about ?? "",
    media: {
      photos: group.media?.photos ?? [],
      teasers: group.media?.teasers ?? [],
    },
  };
}

/** A group's name, text and photos, and below them its events. Tina's groups are edited here too. */
export const EventGroupEdit = () => {
  const { id } = useParams();
  const repo = useEventGroupRepository();
  const query = useEventGroupQuery("edit", id);
  const { open } = useNotification();
  const setTitle = useDocumentTitle();
  const navigate = useNavigate();
  const { setWarnWhen } = useWarnAboutChange();

  const name = query.data?.group.name;
  useEffect(() => {
    if (name) setTitle(`Edit ${name}`);
  }, [name]);

  if (query.isPending) return <GroupLoading />;
  // A failed refresh after a member change keeps what is loaded, so the form and its edits stay.
  if (query.isError && !query.data) {
    return (
      <GroupLoadFailed error={query.error} onRetry={() => query.refetch()} />
    );
  }
  if (!query.data) return <GroupGone />;

  const { group, events } = query.data;

  async function save({ name, about, media }: EventGroupFormType) {
    try {
      // The route's id, not the form's: the form's `id` is a hidden storage path and not to be trusted.
      // The PUT replaces name, text and photos, so all three go, not only what changed.
      await repo.update({ id: id!, name, about, media });
      open?.({ type: "success", message: `Saved ${name}` });
      return true;
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to save the group",
        description: refusalMessage(error),
      });
      return false;
    }
  }

  return (
    <div className="p-6 pb-24">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <BackLink />
          <div className="flex items-center gap-2">
            <Button asChild variant="outline">
              <Link to={`/event-groups/show/${group.id}`}>View</Link>
            </Button>
            <DeleteGroupDialog
              group={group}
              eventCount={events.length}
              // A plain delete leaves on purpose, so the unsaved-changes prompt is cleared first.
              onDone={() => {
                setWarnWhen(false);
                navigate(GROUPS_VIEW);
              }}
            />
          </div>
        </div>
        {query.isError && (
          <p role="alert" className="text-sm text-destructive">
            Could not reload this group. {toHumanErrorMessage(query.error)}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold">Edit event group</h1>
          {group.externalKey && (
            <Badge variant="secondary">Made by Tina's festival job</Badge>
          )}
        </div>
        <EventGroupForm
          key={group.id}
          defaultValues={formValues(group)}
          submitLabel="Save"
          onSave={save}
        />
        <GroupMembersEditor group={group} events={events} />
      </div>
    </div>
  );
};
